import { Badge, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { StatusBadge, statusOptions } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { formatDateTime, useAppToast, useHasRole, usePendingRows } from '../../../helpers'
import { JobPositionService } from '../../../services/JobPosition/JobPositionService'
import { JobPosition, JobPositionStatus, OrganizationalLevel } from '../../../services/JobPosition/types'
import { SectorService } from '../../../services/Sector/SectorService'
import { JOB_POSITION_STATUS, ORGANIZATIONAL_LEVEL_LABELS, ORGANIZATIONAL_LEVEL_OPTIONS } from './constants'
import { JobPositionFormModal } from './JobPositionFormModal'

const INITIAL_FILTERS = { search: '', sectorId: '', level: '', status: '' }

const COLUMNS = 7

export default function JobPositionPage() {
  const service = new JobPositionService()
  const toast = useAppToast()
  const savingRow = usePendingRows()
  const [jobPositions, setJobPositions] = useState<JobPosition[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalPages, setTotalPages] = useState(1)
  const [sectorOptions, setSectorOptions] = useState<{ value: number; label: string }[]>([])
  const [isDeleting, setIsDeleting] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [targetRecord, setTargetRecord] = useState<JobPosition>()
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [recordOpen, setRecordOpen] = useState<JobPosition>()
  const [isViewing, setIsViewing] = useState(false)
  const hasRole = useHasRole()
  const permissions = {
    viewJobs: hasRole('view_jobs'),
    addJob: hasRole('add_job'),
    editJob: hasRole('edit_job'),
    pauseJob: hasRole('pause_job'),
    deleteJob: hasRole('delete_job')
  }

  useEffect(() => {
    new SectorService()
      .listForSelect()
      .then((sectors) => setSectorOptions(sectors.map((sector) => ({ value: sector.id, label: sector.name }))))
      .catch((error) => toast.error('Falha ao carregar setores', error))
  }, [])

  useEffect(() => {
    refreshList()
  }, [filters, page, pageSize])

  const updateFilters = (patch: Partial<typeof INITIAL_FILTERS>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  const isDirty = Object.entries(INITIAL_FILTERS).some(
    ([key, value]) => filters[key as keyof typeof INITIAL_FILTERS] !== value
  )

  const refreshList = async () => {
    setLoading(true)
    setLoadError(false)
    service
      .list({
        search: filters.search || undefined,
        sector_id: filters.sectorId ? Number(filters.sectorId) : undefined,
        organizational_level: (filters.level || undefined) as OrganizationalLevel | undefined,
        status: (filters.status || undefined) as JobPositionStatus | undefined,
        page,
        per_page: pageSize
      })
      .then((result) => {
        // Deleting the only role of the last page leaves it empty: go back to the new last page,
        // still loading, so the deleted row does not show meanwhile.
        if (page > result.last_page) {
          setPage(result.last_page)
          return
        }
        setJobPositions(result.data)
        setTotalPages(result.last_page)
        setLoading(false)
      })
      .catch((error) => {
        setLoadError(true)
        setLoading(false)
        toast.error('Erro ao carregar os cargos', error)
      })
  }

  const handleDelete = async () => {
    if (!targetRecord) return

    setIsDeleting(true)
    try {
      await service.delete(targetRecord.id)
      toast.success('Cargo excluído')
      setOpenDeleteDialog(false)
      refreshList()
    } catch (error) {
      toast.error('Erro ao excluir o cargo', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleStatusChange = (jobPosition: JobPosition, status: JobPositionStatus) =>
    savingRow.run(jobPosition.id, () =>
      service
        .updateStatus(jobPosition.id, status)
        .then((response) => {
          setJobPositions((prev) => prev.map((item) => (item.id === response.id ? response : item)))
          toast.success(status === 'active' ? 'Cargo ativado' : 'Cargo desativado')
        })
        .catch((error) => toast.error('Erro ao alterar o status do cargo', error))
    )

  const openModal = (jobPosition: JobPosition | undefined, viewOnly: boolean) => {
    setRecordOpen(jobPosition)
    setIsViewing(viewOnly)
    setIsFormModalOpen(true)
  }

  return (
    <>
      <PageTitle title="Cadastro de Cargos" description="Cargos da ABRA, com o descritivo e os documentos exigidos" />
      <FilterBar
        onClear={() => {
          setFilters(INITIAL_FILTERS)
          setPage(1)
        }}
        isDirty={isDirty}
      >
        <FilterSearch
          value={filters.search}
          onChange={(search) => updateFilters({ search })}
          placeholder="Buscar por nome do cargo"
          autoFocus
        />
        <FilterSelect
          label="Setor"
          value={filters.sectorId}
          onChange={(sectorId) => updateFilters({ sectorId })}
          options={sectorOptions}
        />
        <FilterSelect
          label="Nível Organizacional"
          value={filters.level}
          onChange={(level) => updateFilters({ level })}
          options={ORGANIZATIONAL_LEVEL_OPTIONS}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(status) => updateFilters({ status })}
          options={statusOptions(JOB_POSITION_STATUS)}
        />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Cargo</Th>
              <Th>Setor</Th>
              <Th>Nível</Th>
              <Th>Status</Th>
              <Th>Criado por</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={COLUMNS} />
            ) : loadError ? (
              <TableErrorRow colSpan={COLUMNS} entity="cargos" onRetry={refreshList} />
            ) : jobPositions.length === 0 ? (
              <TableEmptyRow colSpan={COLUMNS} entity="cargo" />
            ) : (
              jobPositions.map((jobPosition) => (
                <Tr key={jobPosition.id}>
                  <Td>
                    {jobPosition.name}
                    {!jobPosition.is_complete && (
                      <Tooltip label="Falta setor, nível ou algum item do descritivo. Edite o cargo para completar. Até lá ele não pode ser usado em MPs e vagas.">
                        <Badge ml={2} colorScheme="orange">
                          Cadastro incompleto
                        </Badge>
                      </Tooltip>
                    )}
                  </Td>
                  <Td>
                    <EmptyCell value={jobPosition.sector?.name} />
                  </Td>
                  <Td>
                    <EmptyCell
                      value={
                        jobPosition.organizational_level &&
                        ORGANIZATIONAL_LEVEL_LABELS[jobPosition.organizational_level]
                      }
                    />
                  </Td>
                  <Td>
                    <StatusBadge status={jobPosition.status} statuses={JOB_POSITION_STATUS} />
                  </Td>
                  <Td>{jobPosition.creator.name}</Td>
                  <Td>{formatDateTime(jobPosition.created_at, { showSeconds: true })}</Td>
                  <Td>
                    {!permissions.editJob && permissions.viewJobs && (
                      <ActionButton action="view" label="Ver cargo" onClick={() => openModal(jobPosition, true)} />
                    )}
                    {permissions.pauseJob &&
                      (jobPosition.status === 'active' ? (
                        <ActionButton
                          action="deactivate"
                          isDisabled={savingRow.isPending(jobPosition.id)}
                          onClick={() => handleStatusChange(jobPosition, 'inactive')}
                        />
                      ) : (
                        <ActionButton
                          action="activate"
                          isDisabled={savingRow.isPending(jobPosition.id)}
                          onClick={() => handleStatusChange(jobPosition, 'active')}
                        />
                      ))}
                    {permissions.editJob && (
                      <ActionButton action="edit" onClick={() => openModal(jobPosition, false)} />
                    )}
                    {permissions.deleteJob && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setTargetRecord(jobPosition)
                          setOpenDeleteDialog(true)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />

      {targetRecord && (
        <ConfirmDialog
          isOpen={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={handleDelete}
          action="delete"
          entity="cargo"
          name={targetRecord.name}
          warning="Se ele for graduação de outros cargos, sai também da lista de graduações deles."
          isLoading={isDeleting}
        />
      )}

      {permissions.addJob && <ActionFab label="Adicionar cargo" onClick={() => openModal(undefined, false)} />}

      <JobPositionFormModal
        isOpen={isFormModalOpen}
        jobPosition={recordOpen}
        isReadOnly={isViewing}
        onSaved={refreshList}
        onClose={() => setIsFormModalOpen(false)}
      />
    </>
  )
}
